const STATE_KEY = "hydrotools:hydrostack-console:v1";

function esc(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function readState() {
  const fallback = {
    autoRefresh: true,
    refreshMs: 2500,
    selectedSensor: "r2sonic",
    payloadText: "test-packet",
  };
  try {
    const raw = localStorage.getItem(STATE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return {
      ...fallback,
      ...(parsed && typeof parsed === "object" ? parsed : {}),
      refreshMs: Math.max(500, Math.min(15000, Number(parsed?.refreshMs || fallback.refreshMs) || fallback.refreshMs)),
      selectedSensor: ["r2sonic", "posmv", "aml"].includes(String(parsed?.selectedSensor || "").toLowerCase())
        ? String(parsed.selectedSensor).toLowerCase()
        : fallback.selectedSensor,
      payloadText: String(parsed?.payloadText || fallback.payloadText),
      autoRefresh: parsed?.autoRefresh === true,
    };
  } catch {
    return fallback;
  }
}

function writeState(state) {
  try { localStorage.setItem(STATE_KEY, JSON.stringify(state)); } catch {}
}

async function requestJson(url, options = {}) {
  const timeoutMs = Math.max(500, Number(options.timeoutMs || 5000) || 5000);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const response = await fetch(url, {
    method: options.method || "GET",
    signal: controller.signal,
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    body: options.body == null ? undefined : JSON.stringify(options.body),
  }).finally(() => clearTimeout(timer));
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(String(payload?.error || `Request failed (${response.status})`));
  return payload;
}

function statusTone(status) {
  return status === "ok" ? "#22c55e" : "#ef4444";
}

function timestampLabel(value) {
  if (!value) return "n/a";
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleString() : String(value);
}

function asFiniteNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function buildSparkline(values, { width = 520, height = 120 } = {}) {
  if (!Array.isArray(values) || values.length < 2) return "";
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;
  const points = values.map((v, i) => {
    const x = (i / Math.max(1, values.length - 1)) * width;
    let y;
    if (span === 0) {
      y = height / 2;
    } else {
      y = height - ((v - min) / span) * height;
      if (y < 1) y = 1;
      if (y > height - 1) y = height - 1;
    }
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  return `
    <svg viewBox="0 0 ${width} ${height}" width="100%" height="${height}" preserveAspectRatio="none" style="display:block; background:rgba(15,23,42,.35); border:1px solid rgba(148,163,184,.2); border-radius:8px;">
      <polyline fill="none" stroke="#22d3ee" stroke-width="2" points="${points}"></polyline>
    </svg>
  `;
}

// ── Boat-agent download helpers ───────────────────────────────────────────────

function generateAgentMjs() {
  const host = window.location.hostname;
  return `#!/usr/bin/env node
// HydroStack Boat Agent — generated ${new Date().toISOString().slice(0, 10)}
// Run:  node agent.mjs
// Requires Node.js 18+ — no npm install needed

import dgram from "dgram";
import https from "https";

const TARGET_HOST  = ${JSON.stringify(host)};
const ACQ_R2SONIC  = "/api/hydrostack-acq/v1/ingest/r2sonic";
const ACQ_POSMV    = "/api/hydrostack-acq/v1/ingest/posmv";
const CMD_DEQUEUE  = "/api/hydrostack-acq/v1/command/r2sonic/dequeue";
const R2_HOST      = "10.0.1.102";
const R2_CMD_PORT  = 4002;

const SOURCES = [
  { name: "bathy",    port: 4000, path: ACQ_R2SONIC, enabled: true  },
  { name: "snippet",  port: 4001, path: ACQ_R2SONIC, enabled: true  },
  { name: "snippet2", port: 4006, path: ACQ_R2SONIC, enabled: true  },
  { name: "wc",       port: 4005, path: ACQ_R2SONIC, enabled: false },
  { name: "posmv-a",  port: 5602, path: ACQ_POSMV,   enabled: true  },
  { name: "posmv-b",  port: 5606, path: ACQ_POSMV,   enabled: true  },
  { name: "posmv-c",  port: 5608, path: ACQ_POSMV,   enabled: true  },
];

const agent = new https.Agent({ keepAlive: true, maxSockets: 6 });

const cmdSock = dgram.createSocket("udp4");
cmdSock.on("error", () => {});

function encodeR2Command(code, value, isF32) {
  const buf = Buffer.alloc(12);
  buf.writeUInt32BE(0x434D4430, 0);
  buf.write(code.slice(0, 4).padEnd(4, "\\0"), 4, "ascii");
  if (isF32) buf.writeFloatBE(value, 8);
  else buf.writeUInt32BE(Math.round(value) >>> 0, 8);
  return buf;
}

function pollCommands() {
  const req = https.request(
    { hostname: TARGET_HOST, path: CMD_DEQUEUE, method: "GET", agent },
    (res) => {
      if (res.statusCode === 204) { res.resume(); return; }
      let body = "";
      res.on("data", (d) => { body += d; });
      res.on("end", () => {
        try {
          const cmd = JSON.parse(body);
          cmdSock.send(encodeR2Command(cmd.code, cmd.value, cmd.is_f32), R2_CMD_PORT, R2_HOST);
        } catch {}
      });
    }
  );
  req.on("error", () => {});
  req.end();
}
setInterval(pollCommands, 500);

const stats = {};
for (const src of SOURCES) {
  stats[src.name] = { rx: 0, tx: 0, txBytes: 0, errors: 0, dropped: 0, lastErr: "" };
}

let inFlight = 0;
const MAX_IN_FLIGHT = 24;

function post(path, data, name) {
  if (inFlight >= MAX_IN_FLIGHT) { stats[name].dropped++; return; }
  inFlight++;
  const req = https.request({
    hostname: TARGET_HOST, path, method: "POST",
    headers: { "Content-Type": "application/octet-stream", "Content-Length": data.length },
    agent,
  }, (res) => {
    res.resume();
    inFlight--;
    if (res.statusCode === 202) { stats[name].tx++; stats[name].txBytes += data.length; }
    else { stats[name].errors++; stats[name].lastErr = "HTTP " + res.statusCode; }
  });
  req.on("error", (err) => { inFlight--; stats[name].errors++; stats[name].lastErr = err.message; });
  req.end(data);
}

function printStatus() {
  process.stdout.write("\\x1B[2J\\x1B[H");
  const now = new Date().toISOString().replace("T", " ").slice(0, 19);
  console.log("HydroStack Boat Agent  " + now);
  console.log("\\u2500\\u2500\\u2500 \\u2192 https://" + TARGET_HOST + " \\u2500\\u2500\\u2500\\n");
  console.log("  Source       UDP    rx       tx       kB sent  drop  errors");
  console.log("  " + "\\u2500".repeat(62));
  for (const src of SOURCES) {
    if (!src.enabled) continue;
    const s = stats[src.name];
    const err = s.errors ? "  \\u2190 " + s.lastErr : "";
    console.log(
      "  " + src.name.padEnd(12) + String(src.port).padEnd(7) +
      String(s.rx).padEnd(9) + String(s.tx).padEnd(9) +
      (s.txBytes / 1024).toFixed(1).padEnd(9) + String(s.dropped).padEnd(6) + s.errors + err
    );
  }
  console.log("\\n  in-flight: " + inFlight + "/" + MAX_IN_FLIGHT + "\\n  Ctrl-C to stop");
}

let started = 0;
const enabledSources = SOURCES.filter(s => s.enabled);
for (const src of enabledSources) {
  const sock = dgram.createSocket("udp4");
  sock.on("message", (msg) => { stats[src.name].rx++; post(src.path, Buffer.from(msg), src.name); });
  sock.on("error", (err) => { stats[src.name].errors++; stats[src.name].lastErr = err.message; sock.close(); });
  sock.bind(src.port, "0.0.0.0", () => {
    try { sock.setBroadcast(true); } catch {}
    if (++started === enabledSources.length) { console.clear(); setInterval(printStatus, 1000); }
  });
}
`;
}

function generateSnifferMjs() {
  return `#!/usr/bin/env node
// POSMV UDP Sniffer — run on the boat PC to verify packets are arriving
// Stop agent.mjs first, then: node posmv-sniff.js
// Requires Node.js 18+  —  Ctrl-C to stop

import dgram from "dgram";

const PORTS = [5602, 5606, 5608];
let total = 0;

for (const port of PORTS) {
  const sock = dgram.createSocket({ type: "udp4", reuseAddr: true });

  sock.on("message", (msg, rinfo) => {
    total++;
    const ts = new Date().toISOString().slice(11, 23);
    const hex = msg.slice(0, 12).toString("hex").replace(/(..)/g, "$1 ").trim();
    console.log(\`[\${ts}] port \${port} ← \${rinfo.address}:\${rinfo.port}  \${msg.length} bytes  \${hex}\`);
  });

  sock.on("error", (err) => {
    console.error(\`port \${port} ERROR: \${err.message}\`);
  });

  sock.bind(port, "0.0.0.0", () => {
    try { sock.setBroadcast(true); } catch {}
    console.log(\`Listening on UDP \${port}\`);
  });
}

setInterval(() => {
  process.stdout.write(\`\\r  total packets: \${total}  \`);
}, 1000);

console.log("Waiting for POSMV packets on 5602 / 5606 / 5608 ...\\n");
`;
}

function generateStartBat() {
  return `@echo off
title HydroStack Boat Agent
echo.
echo HydroStack Boat Agent
echo =====================
echo.

:check_node
where node >nul 2>nul
if %errorlevel% equ 0 goto :run

echo Node.js not found. Installing automatically...
echo.

REM -- Try winget first (Windows 10 1809+ and Windows 11) --
where winget >nul 2>nul
if %errorlevel% equ 0 (
    echo Using Windows Package Manager ^(winget^)...
    winget install OpenJS.NodeJS.LTS --silent --accept-source-agreements --accept-package-agreements
    if %errorlevel% equ 0 (
        call :refresh_path
        where node >nul 2>nul
        if %errorlevel% equ 0 goto :run
    )
    echo winget failed. Trying PowerShell download...
    echo.
)

REM -- Fallback: download LTS MSI via PowerShell --
echo Fetching Node.js LTS version info...
powershell -NoProfile -ExecutionPolicy Bypass -Command ^
  "$r = Invoke-WebRequest 'https://nodejs.org/dist/index.json' -UseBasicParsing | ConvertFrom-Json;" ^
  "$v = ($r | Where-Object { $_.lts -ne $false } | Select-Object -First 1).version;" ^
  "$u = 'https://nodejs.org/dist/' + $v + '/node-' + $v + '-x64.msi';" ^
  "Write-Host ('Downloading Node.js ' + $v + '...');" ^
  "Invoke-WebRequest $u -OutFile ($env:TEMP + '\\hs_node.msi') -UseBasicParsing;" ^
  "Write-Host 'Installing (this may take a minute)...';" ^
  "Start-Process msiexec -ArgumentList '/i', ($env:TEMP + '\\hs_node.msi'), '/qn', '/norestart' -Wait;" ^
  "Remove-Item ($env:TEMP + '\\hs_node.msi') -Force -ErrorAction SilentlyContinue;" ^
  "Write-Host 'Done.'"

call :refresh_path

where node >nul 2>nul
if %errorlevel% neq 0 (
    echo.
    echo Installation failed or PATH not updated.
    echo Close this window, reopen it, and run start.bat again.
    echo Or install manually: https://nodejs.org/en/download
    echo.
    pause
    exit /b 1
)

echo Node.js installed successfully.
echo.

:run
echo Starting HydroStack Boat Agent...
echo Press Ctrl-C to stop.
echo.
node "%~dp0agent.mjs"
pause
exit /b

:refresh_path
for /f "usebackq delims=" %%P in (\`powershell -NoProfile -Command "[Environment]::GetEnvironmentVariable('Path','Machine')"\`) do set "PATH=%%P;%PATH%"
exit /b
`;
}

function generateStartSh() {
  return `#!/bin/bash
set -e
if ! command -v node &>/dev/null; then
  echo "Node.js not found. Install from: https://nodejs.org/en/download"
  exit 1
fi
echo "Starting HydroStack Boat Agent — press Ctrl-C to stop"
node "$(dirname "$0")/agent.mjs"
`;
}

function downloadText(filename, content, mime = "text/plain") {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// ─────────────────────────────────────────────────────────────────────────────

export const hydrostackConsoleTool = {
  id: "hydrostack-console",
  name: "Hydrostack Console",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, el) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(el);
  },
  serialize() { return this._rt?.snapshot?.() ?? null; },
  restore(app, data) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.restore?.(data);
  },
};

function createRuntime(app) {
  let containerEl = null;
  let state = readState();
  let status = "";
  let pollTimer = null;
  let sequence = 0;
  let snapshot = {
    fetchedAt: "",
    api: null,
    acquisition: null,
    fusion: null,
    fusionTiming: null,
    posmvMotion: null,
    offset: null,
    services: [],
    control: null,
  };
  let logs = [];
  let offsetHistory = [];
  let refreshInFlight = false;
  let lastOffsetSampleKey = "";
  let lastOffsetSonarRecv = "";
  let lastOffsetPosmvRecv = "";
  let prevAcqPackets = -1;
  let agentLastActiveMs = 0;

  function pushLog(message, level = "info") {
    logs.unshift({
      ts: new Date().toISOString(),
      level,
      message: String(message || ""),
    });
    logs = logs.slice(0, 100);
  }

  function persist() {
    writeState(state);
  }

  function pullStateFromForm() {
    if (!containerEl) return;
    state.autoRefresh = containerEl.querySelector("#hydrostack-auto-refresh")?.checked !== false;
    state.refreshMs = Math.max(500, Math.min(15000, Number(containerEl.querySelector("#hydrostack-refresh-ms")?.value || 2500) || 2500));
    state.selectedSensor = String(containerEl.querySelector("#hydrostack-sensor")?.value || "r2sonic").trim().toLowerCase();
    state.payloadText = String(containerEl.querySelector("#hydrostack-payload")?.value || "");
    persist();
  }

  function renderCard(title, data, extra = "") {
    const statusText = String(data?.status || "offline");
    const natsText = String(data?.nats || "n/a");
    const packetsIngested = Number.isFinite(Number(data?.packets_ingested)) ? Number(data.packets_ingested).toLocaleString() : "-";
    const packetsObserved = Number.isFinite(Number(data?.packets_observed)) ? Number(data.packets_observed).toLocaleString() : "-";
    return `
      <div class="card" style="padding:10px; min-width:0;">
        <div class="row" style="justify-content:space-between;">
          <div style="font-weight:700;">${esc(title)}</div>
          <div class="small" style="color:${statusTone(statusText)}; font-weight:700;">${esc(statusText.toUpperCase())}</div>
        </div>
        <div class="small" style="margin-top:6px;">NATS: ${esc(natsText)}</div>
        <div class="small" style="margin-top:4px;">Ingested: ${esc(packetsIngested)}</div>
        <div class="small" style="margin-top:2px;">Observed: ${esc(packetsObserved)}</div>
        ${extra}
      </div>
    `;
  }

  function renderOffsetPanel() {
    const history = offsetHistory.map((item) => item.offsetMS);
    const sparkline = buildSparkline(history);
    const latest = offsetHistory.length ? offsetHistory[offsetHistory.length - 1] : null;
    const current = asFiniteNumber(snapshot?.offset?.offsetMS) ?? asFiniteNumber(latest?.offsetMS);
    const min = history.length ? Math.min(...history) : null;
    const max = history.length ? Math.max(...history) : null;
    const basis = snapshot?.offset?.basis || latest?.basis || "n/a";
    const samples = history.length;
    return `
      <details class="toolset-group" open style="margin-top:10px;">
        <summary>Realtime Time Offset</summary>
        <div class="toolset-content">
          <div class="small" style="margin-top:6px;">Offset plotted as <code>posmv_time_seconds - sonar_candidate_time_seconds</code>.</div>
          <div class="small" style="margin-top:4px;">Basis: <code>${esc(basis)}</code> | Samples: ${esc(String(samples))}</div>
          <div class="small" style="margin-top:4px;">Current: <strong>${esc(current == null ? "n/a" : `${current.toFixed(1)} ms`)}</strong> | Min: ${esc(min == null ? "n/a" : `${min.toFixed(1)} ms`)} | Max: ${esc(max == null ? "n/a" : `${max.toFixed(1)} ms`)}</div>
          <div style="margin-top:8px;">${sparkline || '<div class="small">Need at least 2 samples to plot.</div>'}</div>
        </div>
      </details>
    `;
  }

  function computeOffsetSample(fusionTiming, posmvMotion) {
    const posmvSec = asFiniteNumber(posmvMotion?.latest?.time_seconds);
    if (posmvSec == null) return null;
    const cands = fusionTiming?.sensor_time_candidates || {};
    const candidateMap = {
      sensor_time_seconds_mod_week_gps: asFiniteNumber(cands?.sonar_sensor_time_seconds_mod_week_gps_sun0),
      sensor_time_seconds_mod_week_unix: asFiniteNumber(cands?.sonar_sensor_time_seconds_mod_week_unix),
      sensor_time_seconds_direct: asFiniteNumber(cands?.sonar_sensor_time_seconds),
    };
    const preferred = String(fusionTiming?.interpolation_basis || "").trim();
    let basis = "";
    let sonarSec = null;
    if (preferred && candidateMap[preferred] != null) {
      basis = preferred;
      sonarSec = candidateMap[preferred];
    } else {
      let bestAbs = Number.POSITIVE_INFINITY;
      for (const [key, val] of Object.entries(candidateMap)) {
        if (val == null) continue;
        const abs = Math.abs(posmvSec - val);
        if (abs < bestAbs) {
          bestAbs = abs;
          basis = key;
          sonarSec = val;
        }
      }
    }
    if (sonarSec == null) return null;
    const offsetSec = posmvSec - sonarSec;
    const pingNumber = fusionTiming?.latest_ping?.ping_number;
    const sonarRecv = String(fusionTiming?.latest_ping?.received_at_utc || "");
    const posmvRecv = String(posmvMotion?.latest?.received_at_utc || "");
    const sampleKey = `${pingNumber ?? "na"}|${sonarRecv}|${posmvRecv}`;
    return {
      ts: new Date().toISOString(),
      basis,
      posmvSec,
      sonarSec,
      offsetMS: offsetSec * 1000,
      sampleKey,
      sonarRecv,
      posmvRecv,
    };
  }

  function renderAgentPanel() {
    const acqPackets = Number(snapshot?.acquisition?.packets_ingested ?? 0);
    const agentAge = agentLastActiveMs > 0 ? Date.now() - agentLastActiveMs : Infinity;
    const isActive = acqPackets > 0 && agentAge < 10000;
    const isStale  = acqPackets > 0 && !isActive;
    const dotColor = isActive ? "#22c55e" : isStale ? "#f59e0b" : "#64748b";
    const dotGlow  = isActive ? "box-shadow:0 0 7px #22c55e88;" : "";
    const label    = isActive
      ? `Agent active — ${acqPackets.toLocaleString()} packets received`
      : isStale
      ? `Agent stale — ${acqPackets.toLocaleString()} packets received (stopped?)`
      : "Agent not connected";
    const labelColor = isActive ? "#86efac" : isStale ? "#fde68a" : "#94a3b8";

    return `
      <details class="toolset-group" style="margin-top:10px;">
        <summary>Boat Agent Setup</summary>
        <div class="toolset-content">
          <div class="row" style="gap:8px; align-items:center; margin-top:8px;">
            <div style="width:9px;height:9px;border-radius:50%;background:${dotColor};${dotGlow}flex-shrink:0;"></div>
            <span class="small" style="color:${labelColor}; font-weight:600;">${esc(label)}</span>
          </div>
          <div class="small" style="margin-top:10px; color:#94a3b8;">
            Download to the boat PC, put both files in the same folder, then double-click <code>start.bat</code>
            (Windows) or run <code>node agent.mjs</code>. Requires Node.js 18+.
          </div>
          <div class="row" style="margin-top:10px; gap:8px; flex-wrap:wrap;">
            <button id="hs-dl-agent" class="btn primary">Download agent.mjs</button>
            <button id="hs-dl-bat" class="btn">Download start.bat (Windows)</button>
            <button id="hs-dl-sh" class="btn">Download start.sh (Mac/Linux)</button>
          </div>
          <div class="small" style="margin-top:8px;">
            Node.js: <a href="https://nodejs.org/en/download" target="_blank" rel="noopener" style="color:#38bdf8;">nodejs.org/en/download</a>
          </div>

          <details style="margin-top:12px;">
            <summary class="small" style="cursor:pointer; color:#94a3b8; font-weight:600; list-style:none;">&#9654; POSMV Windows Firewall &amp; Diagnostics</summary>
            <div style="margin-top:8px;">
              <div class="small" style="color:#94a3b8; margin-bottom:4px;">1. Open PowerShell as Administrator on the boat PC and run to allow POSMV UDP inbound:</div>
              <pre style="margin:0; padding:8px 10px; background:#0f172a; border-radius:6px; border:1px solid #334155; color:#7dd3fc; font-size:11px; overflow-x:auto; white-space:pre; user-select:all; cursor:text;">New-NetFirewallRule -DisplayName "POSMV UDP 5602" -Direction Inbound -Protocol UDP -LocalPort 5602 -Action Allow
New-NetFirewallRule -DisplayName "POSMV UDP 5606" -Direction Inbound -Protocol UDP -LocalPort 5606 -Action Allow
New-NetFirewallRule -DisplayName "POSMV UDP 5608" -Direction Inbound -Protocol UDP -LocalPort 5608 -Action Allow</pre>
              <div class="small" style="color:#94a3b8; margin-top:10px; margin-bottom:4px;">2. Run the sniffer on the boat PC to see every incoming packet and its source IP (stop agent.mjs first):</div>
              <div class="row" style="margin:6px 0 8px; gap:8px;">
                <button id="hs-dl-sniffer" class="btn">Download posmv-sniff.js</button>
                <span class="small" style="color:#64748b; align-self:center;">node posmv-sniff.js</span>
              </div>
              <div class="small" style="color:#94a3b8; margin-top:6px; margin-bottom:4px;">Or manually test a port (waits 5 s per port):</div>
              <pre style="margin:0; padding:8px 10px; background:#0f172a; border-radius:6px; border:1px solid #334155; color:#7dd3fc; font-size:11px; overflow-x:auto; white-space:pre; user-select:all; cursor:text;">foreach ($port in 5602,5606,5608) {
  $udp = New-Object System.Net.Sockets.UdpClient($port)
  $udp.Client.ReceiveTimeout = 5000
  try   { $b = $udp.Receive([ref]$null); Write-Host "OK  port $port — got $($b.Length) bytes" }
  catch { Write-Host "NO  port $port — no data (timeout or port blocked)" }
  $udp.Close()
}</pre>
              <div class="small" style="color:#94a3b8; margin-top:10px; margin-bottom:4px;">3. Check server-side POSMV counters (run from any terminal with curl):</div>
              <pre style="margin:0; padding:8px 10px; background:#0f172a; border-radius:6px; border:1px solid #334155; color:#7dd3fc; font-size:11px; overflow-x:auto; white-space:pre; user-select:all; cursor:text;">curl.exe -s https://app.automatedoceansystems.com/api/hydrostack-fusion/v1/posmv/motion/state</pre>
              <div class="small" style="color:#64748b; margin-top:6px;">grp102_seen should be &gt; 0 once packets reach the server.</div>
            </div>
          </details>
        </div>
      </details>
    `;
  }

  function render() {
    if (!containerEl) return;
    const services = Array.isArray(snapshot.services) ? snapshot.services : [];
    containerEl.innerHTML = `
      <div class="card" style="padding:10px;">
        <div class="lm-title">Hydrostack Console</div>
        <div class="small" style="margin-top:6px;">Live health, ingest counters, and simulation packet controls for the local Hydrostack backend.</div>

        <details class="toolset-group" open style="margin-top:10px;">
          <summary>System Status</summary>
          <div class="toolset-content">
            <div class="row" style="gap:8px; flex-wrap:wrap;">
              ${renderCard("API", snapshot.api)}
              ${renderCard("Acquisition", snapshot.acquisition)}
              ${renderCard("Fusion", snapshot.fusion)}
            </div>
            <div class="small" style="margin-top:8px;">Last refresh: ${esc(timestampLabel(snapshot.fetchedAt))}</div>
            <div class="small" style="margin-top:4px;">Services advertised: ${services.map((svc) => esc(`${svc.name} (${svc.url})`)).join(" | ") || "n/a"}</div>
            <div class="small" style="margin-top:4px;">Control: ${esc(snapshot?.control?.active ? `running ${snapshot.control.active.action}` : "idle")}</div>
            <div class="row" style="margin-top:10px; gap:10px; flex-wrap:wrap;">
              <button id="hydrostack-refresh" class="btn">Refresh</button>
              <button id="hydrostack-start-stack" class="btn primary">Start Hydrostack</button>
              <button id="hydrostack-stop-stack" class="btn">Stop Hydrostack</button>
              <label class="small"><input id="hydrostack-auto-refresh" type="checkbox" ${state.autoRefresh ? "checked" : ""}/> Auto-refresh</label>
              <label class="small">Interval (ms)
                <input id="hydrostack-refresh-ms" type="number" min="500" max="15000" step="100" value="${esc(String(state.refreshMs))}" style="width:110px; margin-left:6px;" />
              </label>
            </div>
            <div class="small" style="margin-top:8px;">Last command: ${esc(snapshot?.control?.last ? `${snapshot.control.last.action} ${snapshot.control.last.status} (${snapshot.control.last.code})` : "n/a")}</div>
          </div>
        </details>

        <details class="toolset-group" open style="margin-top:10px;">
          <summary>Simulation Packet</summary>
          <div class="toolset-content">
            <div class="row" style="margin-top:6px; gap:10px; flex-wrap:wrap;">
              <label class="small">Sensor
                <select id="hydrostack-sensor" style="margin-left:6px; width:120px;">
                  <option value="r2sonic" ${state.selectedSensor === "r2sonic" ? "selected" : ""}>R2Sonic</option>
                  <option value="posmv" ${state.selectedSensor === "posmv" ? "selected" : ""}>POS MV</option>
                  <option value="aml" ${state.selectedSensor === "aml" ? "selected" : ""}>AML</option>
                </select>
              </label>
            </div>
            <div class="row" style="margin-top:8px;">
              <input id="hydrostack-payload" type="text" value="${esc(state.payloadText)}" placeholder="payload text" style="flex:1;" />
            </div>
            <div class="row" style="margin-top:8px; gap:8px;">
              <button id="hydrostack-send-packet" class="btn primary">Send Packet</button>
              <button id="hydrostack-send-burst" class="btn">Send 25 Packet Burst</button>
            </div>
          </div>
        </details>

        ${renderOffsetPanel()}

        ${renderAgentPanel()}

        <details class="toolset-group" open style="margin-top:10px;">
          <summary>Activity Log</summary>
          <div class="toolset-content" style="max-height:220px; overflow:auto;">
            ${logs.length
              ? logs.map((item) => `<div class="small" style="margin-top:4px;"><code>${esc(timestampLabel(item.ts))}</code> [${esc(item.level)}] ${esc(item.message)}</div>`).join("")
              : '<div class="small">No activity yet.</div>'}
          </div>
        </details>

        <div class="small" style="margin-top:10px; color:${status ? "#cbd5e1" : "#94a3b8"};">${esc(status || "Ready.")}</div>
      </div>
    `;
  }

  async function refreshStatus({ quiet = false } = {}) {
    if (refreshInFlight) return;
    refreshInFlight = true;
    try {
      const tasks = [
        requestJson("/api/hydrostack-api/healthz", { timeoutMs: 2500 }),
        requestJson("/api/hydrostack-acq/healthz", { timeoutMs: 2500 }),
        requestJson("/api/hydrostack-fusion/healthz", { timeoutMs: 2500 }),
        requestJson("/api/hydrostack-api/v1/system/services", { timeoutMs: 2500 }),
        requestJson("/api/hydrostack/control/status", { timeoutMs: 2500 }),
        requestJson("/api/hydrostack-fusion/v1/fusion/timing/state", { timeoutMs: 2500 }),
        requestJson("/api/hydrostack-fusion/v1/posmv/motion/state", { timeoutMs: 2500 }),
      ];
      const results = await Promise.allSettled(tasks);
      const api = results[0]?.status === "fulfilled" ? results[0].value : snapshot.api;
      const acquisition = results[1]?.status === "fulfilled" ? results[1].value : snapshot.acquisition;
      const fusion = results[2]?.status === "fulfilled" ? results[2].value : snapshot.fusion;
      const servicesPayload = results[3]?.status === "fulfilled" ? results[3].value : { services: snapshot.services || [] };
      const control = results[4]?.status === "fulfilled" ? results[4].value : snapshot.control;
      const fusionTiming = results[5]?.status === "fulfilled" ? results[5].value : snapshot.fusionTiming;
      const posmvMotion = results[6]?.status === "fulfilled" ? results[6].value : snapshot.posmvMotion;

      const acqPackets = Number(acquisition?.packets_ingested ?? -1);
      if (acqPackets > 0 && acqPackets !== prevAcqPackets) agentLastActiveMs = Date.now();
      prevAcqPackets = acqPackets;

      const offset = computeOffsetSample(fusionTiming, posmvMotion);
      const hasFreshSonar = Boolean(offset?.sonarRecv) && offset.sonarRecv !== lastOffsetSonarRecv;
      const hasFreshPosmv = Boolean(offset?.posmvRecv) && offset.posmvRecv !== lastOffsetPosmvRecv;
      if (offset && offset.sampleKey && offset.sampleKey !== lastOffsetSampleKey && hasFreshSonar && hasFreshPosmv) {
        offsetHistory.push(offset);
        lastOffsetSampleKey = offset.sampleKey;
        lastOffsetSonarRecv = offset.sonarRecv;
        lastOffsetPosmvRecv = offset.posmvRecv;
        if (offsetHistory.length > 480) {
          offsetHistory = offsetHistory.slice(offsetHistory.length - 480);
        }
      }
      snapshot = {
        fetchedAt: new Date().toISOString(),
        api,
        acquisition,
        fusion,
        fusionTiming,
        posmvMotion,
        offset,
        services: Array.isArray(servicesPayload?.services) ? servicesPayload.services : [],
        control,
      };
      if (!quiet) {
        status = "Hydrostack status refreshed.";
        pushLog("Status refresh complete.");
      }
    } catch (error) {
      status = error?.message || "Failed to refresh Hydrostack status.";
      pushLog(status, "error");
    } finally {
      refreshInFlight = false;
      render();
    }
  }

  async function startHydrostack() {
    try {
      status = "Starting Hydrostack services...";
      render();
      const response = await requestJson("/api/hydrostack/control/start", { method: "POST" });
      const result = response?.result || {};
      const summary = `Start command ${result?.status || "done"} (code ${result?.code ?? "?"}).`;
      pushLog(summary);
      if (Array.isArray(result?.output) && result.output.length) {
        pushLog(result.output[result.output.length - 1]);
      }
      status = "Hydrostack start command completed.";
      await refreshStatus({ quiet: true });
      render();
    } catch (error) {
      status = error?.message || "Failed to start Hydrostack.";
      pushLog(status, "error");
      render();
    }
  }

  async function stopHydrostack() {
    try {
      status = "Stopping Hydrostack services...";
      render();
      const response = await requestJson("/api/hydrostack/control/stop", { method: "POST" });
      const result = response?.result || {};
      const summary = `Stop command ${result?.status || "done"} (code ${result?.code ?? "?"}).`;
      pushLog(summary);
      if (Array.isArray(result?.output) && result.output.length) {
        pushLog(result.output[result.output.length - 1]);
      }
      status = "Hydrostack stop command completed.";
      await refreshStatus({ quiet: true });
      render();
    } catch (error) {
      status = error?.message || "Failed to stop Hydrostack.";
      pushLog(status, "error");
      render();
    }
  }

  async function sendSimPacket() {
    pullStateFromForm();
    const sensor = ["r2sonic", "posmv", "aml"].includes(state.selectedSensor) ? state.selectedSensor : "r2sonic";
    const payloadText = String(state.payloadText || "").trim() || "test-packet";
    const payloadBase64 = btoa(unescape(encodeURIComponent(payloadText)));
    try {
      const response = await requestJson("/api/hydrostack-acq/v1/sim/packet", {
        method: "POST",
        body: {
          sensor,
          payload_base64: payloadBase64,
          sleep_ms: 0,
          source_file: "hydrotools-console",
          sequence,
        },
      });
      sequence += 1;
      status = `Queued ${sensor} packet sequence ${response?.sequence ?? sequence - 1}.`;
      pushLog(status);
      await refreshStatus({ quiet: true });
      render();
    } catch (error) {
      status = error?.message || "Packet send failed.";
      pushLog(status, "error");
      render();
    }
  }

  async function sendBurst() {
    pullStateFromForm();
    for (let i = 0; i < 25; i += 1) {
      await sendSimPacket();
    }
    status = "Sent 25 simulated packets.";
    pushLog(status);
    render();
  }

  function startPolling() {
    stopPolling();
    if (!state.autoRefresh) return;
    pollTimer = setInterval(() => {
      refreshStatus({ quiet: true });
    }, state.refreshMs);
  }

  function stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  function onInput() {
    pullStateFromForm();
    startPolling();
  }

  function onClick(event) {
    if (event.target?.closest?.("#hydrostack-refresh")) {
      refreshStatus();
      return;
    }
    if (event.target?.closest?.("#hydrostack-start-stack")) {
      startHydrostack();
      return;
    }
    if (event.target?.closest?.("#hydrostack-stop-stack")) {
      stopHydrostack();
      return;
    }
    if (event.target?.closest?.("#hydrostack-send-packet")) {
      sendSimPacket();
      return;
    }
    if (event.target?.closest?.("#hydrostack-send-burst")) {
      sendBurst();
      return;
    }
    if (event.target?.closest?.("#hs-dl-agent")) {
      downloadText("agent.mjs", generateAgentMjs());
      return;
    }
    if (event.target?.closest?.("#hs-dl-bat")) {
      downloadText("start.bat", generateStartBat());
      return;
    }
    if (event.target?.closest?.("#hs-dl-sh")) {
      downloadText("start.sh", generateStartSh());
      return;
    }
    if (event.target?.closest?.("#hs-dl-sniffer")) {
      downloadText("posmv-sniff.js", generateSnifferMjs());
      return;
    }
  }

  function mount(el) {
    if (containerEl) {
      containerEl.removeEventListener("input", onInput);
      containerEl.removeEventListener("click", onClick);
    }
    containerEl = el;
    render();
    containerEl.addEventListener("input", onInput);
    containerEl.addEventListener("click", onClick);
    if (state.autoRefresh) refreshStatus({ quiet: true });
    startPolling();
  }

  function destroy() {
    stopPolling();
    if (!containerEl) return;
    containerEl.removeEventListener("input", onInput);
    containerEl.removeEventListener("click", onClick);
    containerEl = null;
  }

  function snapshotState() {
    return {
      version: 1,
      state: { ...state },
    };
  }

  function restore(data) {
    const next = data?.state;
    if (!next || typeof next !== "object") return;
    state = {
      ...state,
      ...next,
      autoRefresh: next.autoRefresh === true,
      refreshMs: Math.max(500, Math.min(15000, Number(next.refreshMs || state.refreshMs) || state.refreshMs)),
      selectedSensor: ["r2sonic", "posmv", "aml"].includes(String(next.selectedSensor || "").toLowerCase())
        ? String(next.selectedSensor).toLowerCase()
        : state.selectedSensor,
      payloadText: String(next.payloadText || state.payloadText || ""),
    };
    persist();
    render();
    startPolling();
  }

  return {
    mount,
    destroy,
    snapshot: snapshotState,
    restore,
  };
}
